import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/lib/auth-context";
import { checkPatchConflicts, listPatches, movePatch, uploadPatches } from "@/lib/patches-api";
import { formatBytes } from "@/lib/format-bytes";
import { AccountShell } from "@/components/account/AccountShell";
import { UploadProgressCard } from "@/components/account/UploadProgressCard";
import { PATCHES_ADMIN } from "@/constants/testIds";

const LOCATION_LABEL = { data: "Data", events: "Events" };

const describeError = (err) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.cross_location_conflicts) {
    return `Soubor se stejným názvem už je ve druhé složce: ${detail.cross_location_conflicts.join(", ")}`;
  }
  if (detail?.conflicts) return `Soubory už existují: ${detail.conflicts.join(", ")}`;
  return "Zkus to znovu.";
};

export const PatchesAdminPage = () => {
  const { user, loading } = useAuth();
  const [existing, setExisting] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isEventPatch, setIsEventPatch] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState(null);
  const [movingName, setMovingName] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const speedRef = useRef({ time: 0, loaded: 0, speedBps: 0 });

  const refresh = useCallback(() => {
    listPatches()
      .then(setExisting)
      .catch(() => toast("Nepodařilo se načíst seznam patchů"));
  }, []);

  useEffect(() => {
    if (user?.is_gm) refresh();
  }, [user, refresh]);

  const handleFileChange = (e) => {
    setSelectedFiles([...e.target.files]);
    e.target.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (uploading) return;
    dragCounterRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (uploading) return;
    if (e.dataTransfer.files?.length) setSelectedFiles([...e.dataTransfer.files]);
  };

  const doUpload = async (overwrite, location) => {
    setUploading(true);
    speedRef.current = { time: performance.now(), loaded: 0, speedBps: 0 };
    setUploadStats({ percent: 0, loaded: 0, total: selectedFiles.reduce((sum, f) => sum + f.size, 0) });
    try {
      const { saved } = await uploadPatches(selectedFiles, overwrite, location, (evt) => {
        const now = performance.now();
        const s = speedRef.current;
        const dt = (now - s.time) / 1000;
        if (dt > 0.2) {
          s.speedBps = (evt.loaded - s.loaded) / dt;
          s.time = now;
          s.loaded = evt.loaded;
        }
        setUploadStats({
          percent: evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
          loaded: evt.loaded,
          total: evt.total || 0,
          speedBps: s.speedBps,
        });
      });
      toast("Nahráno", { description: `${saved.length} soubor(ů) uloženo do ${LOCATION_LABEL[location]}.` });
      setSelectedFiles([]);
      setPendingAction(null);
      refresh();
    } catch (err) {
      toast("Nahrání se nezdařilo", { description: describeError(err) });
    } finally {
      setUploading(false);
      setUploadStats(null);
    }
  };

  const handleUploadClick = async () => {
    if (selectedFiles.length === 0) return;
    const location = isEventPatch ? "events" : "data";
    try {
      const { conflicts, cross_location_conflicts: crossConflicts } = await checkPatchConflicts(
        selectedFiles.map((f) => f.name),
        location
      );
      if (crossConflicts.length > 0) {
        toast("Nelze nahrát", {
          description: `Stejný název už je ve druhé složce (Data a events se nesmí překrývat): ${crossConflicts.join(", ")}`,
        });
        return;
      }
      if (conflicts.length > 0) {
        setPendingAction({ kind: "upload", names: conflicts, location });
        return;
      }
      await doUpload(false, location);
    } catch {
      toast("Nepodařilo se ověřit existující soubory");
    }
  };

  const doMove = async (name, fromLocation, toLocation, overwrite) => {
    setMovingName(name);
    try {
      await movePatch(name, fromLocation, toLocation, overwrite);
      toast("Přesunuto", { description: `${name} je teď v ${LOCATION_LABEL[toLocation]}.` });
      setPendingAction(null);
      refresh();
    } catch (err) {
      toast("Přesun se nezdařil", { description: describeError(err) });
    } finally {
      setMovingName(null);
    }
  };

  const handleMoveClick = async (file) => {
    const toLocation = file.location === "data" ? "events" : "data";
    try {
      // Only the destination's same-location conflicts matter here - the
      // file trivially already exists in its own (source) location, that's
      // not a cross-location conflict, it's the thing being moved.
      const { conflicts } = await checkPatchConflicts([file.name], toLocation);
      if (conflicts.length > 0) {
        setPendingAction({ kind: "move", names: conflicts, name: file.name, fromLocation: file.location, toLocation });
        return;
      }
      await doMove(file.name, file.location, toLocation, false);
    } catch {
      toast("Nepodařilo se ověřit cílovou složku");
    }
  };

  const handleConfirmPendingAction = () => {
    if (!pendingAction) return;
    if (pendingAction.kind === "upload") {
      doUpload(true, pendingAction.location);
    } else {
      doMove(pendingAction.name, pendingAction.fromLocation, pendingAction.toLocation, true);
    }
  };

  if (loading) {
    return (
      <section className="min-h-[70vh] px-6 pb-24 pt-40 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-white/40">Načítám…</p>
      </section>
    );
  }

  if (!user) return <Navigate to="/ucet" replace />;

  if (!user.is_gm) {
    return (
      <AccountShell isGm={false} testId={PATCHES_ADMIN.guard}>
        <div className="mx-auto max-w-lg rounded-xl border border-white/10 bg-[#17171b] p-8 text-center">
          <p className="text-sm text-white/60">Správa patchů je jen pro GM.</p>
          <Link to="/ucet" className="mt-4 inline-block text-sm text-[#F0801F] hover:underline">
            Zpět na účet
          </Link>
        </div>
      </AccountShell>
    );
  }

  return (
    <AccountShell isGm={user.is_gm} testId={PATCHES_ADMIN.page}>
      <div className="mb-8">
        <p className="font-display text-xs font-600 uppercase tracking-[0.3em] text-[#F0801F]">
          Nástroje GM
        </p>
        <h1 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight text-white sm:text-4xl">
          Patche
        </h1>
        <p className="mt-3 max-w-xl text-sm text-white/50">
          Nahrané soubory jdou do <code className="text-white/70">downloads/game/Data</code> (nebo{" "}
          <code className="text-white/70">events</code>, viz zaškrtávátko níže), odkud si je launcher stahuje.
          Soubory mohou být velké (klidně jednotky GB) - upload podle rychlosti připojení může chvíli trvat.
        </p>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#17171b] p-6">
        <p className="mb-4 font-display text-xs font-600 uppercase tracking-[0.16em] text-white/50">
          Nahrát nové soubory
        </p>
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          data-testid={PATCHES_ADMIN.dropzone}
          className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
            isDragging
              ? "border-[#F0801F] bg-[#F0801F]/10"
              : "border-white/15 bg-white/[0.02] hover:border-white/30"
          } ${uploading ? "pointer-events-none opacity-60" : ""}`}
        >
          <i className="fa-solid fa-cloud-arrow-up text-2xl text-white/40" />
          <p className="text-sm text-white/70">Přetáhni soubory sem, nebo klikni pro výběr</p>
          <p className="text-xs text-white/40">Můžeš vybrat i více souborů najednou, klidně i velké (GB)</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={uploading}
            data-testid={PATCHES_ADMIN.fileInput}
            className="hidden"
          />
        </div>

        <div className="mt-4 flex items-center gap-2.5">
          <Checkbox
            id="patches-event-checkbox"
            checked={isEventPatch}
            onCheckedChange={(v) => setIsEventPatch(v === true)}
            disabled={uploading}
            data-testid={PATCHES_ADMIN.eventCheckbox}
          />
          <label htmlFor="patches-event-checkbox" className="cursor-pointer select-none text-sm text-white/70">
            Eventové{" "}
            <span className="text-white/40">
              (nahraje se do <code className="text-white/50">events/</code> místo Data - stejný název souboru
              nesmí být na obou místech zároveň)
            </span>
          </label>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-4 space-y-1.5">
            {selectedFiles.map((f) => (
              <div
                key={f.name}
                data-testid={PATCHES_ADMIN.selectedRow}
                className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/70"
              >
                <span className="truncate">{f.name}</span>
                <span className="shrink-0 text-white/40">{formatBytes(f.size)}</span>
              </div>
            ))}
          </div>
        )}

        {uploading && uploadStats && (
          <UploadProgressCard
            percent={uploadStats.percent}
            loaded={uploadStats.loaded}
            total={uploadStats.total}
            speedBps={uploadStats.speedBps}
            fileCount={selectedFiles.length}
          />
        )}

        <button
          onClick={handleUploadClick}
          disabled={selectedFiles.length === 0 || uploading}
          data-testid={PATCHES_ADMIN.uploadButton}
          className="mt-4 w-full rounded-md bg-[#F0801F] py-2.5 font-display text-xs font-600 uppercase tracking-[0.14em] text-[#141417] hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {uploading ? "Nahrávám…" : "Nahrát"}
        </button>
      </div>

      <div className="mt-6 space-y-2">
        <p className="mb-1 font-display text-xs font-600 uppercase tracking-[0.16em] text-white/50">
          Aktuální soubory
        </p>
        {existing === null && <p className="text-sm text-white/40">Načítám…</p>}
        {existing?.length === 0 && <p className="text-sm text-white/50">Zatím žádné soubory.</p>}
        {existing?.map((f) => (
          <div
            key={`${f.location}/${f.name}`}
            data-testid={PATCHES_ADMIN.existingRow}
            className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-[#17171b] px-5 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                    f.location === "events"
                      ? "bg-[#F0801F]/15 text-[#F0801F]"
                      : "bg-white/10 text-white/50"
                  }`}
                >
                  {LOCATION_LABEL[f.location]}
                </span>
                <span className="truncate text-sm text-white/80">{f.name}</span>
              </div>
            </div>
            <span className="shrink-0 text-xs text-white/40">{formatBytes(f.size)}</span>
            <button
              onClick={() => handleMoveClick(f)}
              disabled={movingName === f.name}
              data-testid={PATCHES_ADMIN.moveButton}
              className="shrink-0 rounded-md border border-white/20 px-2.5 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-[#F0801F]/60 hover:text-[#F0801F] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {movingName === f.name ? "Přesouvám…" : `→ ${f.location === "data" ? "Events" : "Data"}`}
            </button>
          </div>
        ))}
      </div>

      <Dialog open={pendingAction !== null} onOpenChange={(next) => !next && setPendingAction(null)}>
        <DialogContent
          data-testid={PATCHES_ADMIN.overwriteDialog}
          className="border-white/10 bg-[#17171b] text-white sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Soubor už existuje</DialogTitle>
            <DialogDescription className="text-white/50">
              {pendingAction?.kind === "upload"
                ? "Tyto soubory už v cílové složce jsou - opravdu je chceš přepsat?"
                : "V cílové složce už soubor se stejným názvem je - opravdu ho chceš přepsat?"}
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-white/70">
            {pendingAction?.names.map((name) => (
              <li key={name} className="truncate">
                {name}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <button
              onClick={() => setPendingAction(null)}
              data-testid={PATCHES_ADMIN.overwriteCancelButton}
              className="rounded-md border border-white/20 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:border-white/40"
            >
              Zrušit
            </button>
            <button
              onClick={handleConfirmPendingAction}
              data-testid={PATCHES_ADMIN.overwriteConfirmButton}
              className="rounded-md bg-[#F0801F] px-4 py-2 text-xs font-600 uppercase tracking-wide text-[#141417] hover:bg-[#ff9438]"
            >
              Přepsat
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountShell>
  );
};
